import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  readYesNoDisplayFromAnswer,
  yesNoDisplayToLoincCoding,
} from "@/components/ClearableYesNoRadioField";
import {
  MEMBRANES_DATE_LINK_ID,
  MEMBRANES_DATE_TIME_LINK_ID,
  MEMBRANES_FERN_RESULTS_LINK_ID,
  MEMBRANES_FLUID_COLOR_LINK_ID,
  MEMBRANES_FLUID_COLOR_OTHER_LINK_ID,
  MEMBRANES_NITRAZINE_LINK_ID,
  MEMBRANES_NOTES_LINK_ID,
  MEMBRANES_POOLING_LINK_ID,
  MEMBRANES_ROOT_LINK_ID,
  MEMBRANES_SIGNATURE_INITIALS_LINK_ID,
  MEMBRANES_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/constants/membranes-link-ids";
import type { MembranesFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/types/membranes-form";
import { getEmptyMembranesFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/utils/membranes-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  positiveNegativeDisplayToCoding,
  readPositiveNegativeDisplayFromAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-positive-negative-coding";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";

export function mapQuestionnaireResponseToMembranesFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): MembranesFlowsheetFormValues {
  // Use empty values so a missing or malformed response leaves date/time blank.
  const emptyValues = getEmptyMembranesFlowsheetFormValues();
  const root = findQuestionnaireResponseItem(questionnaireResponse?.item, MEMBRANES_ROOT_LINK_ID);
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, MEMBRANES_DATE_TIME_LINK_ID);
  const dateAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, MEMBRANES_DATE_LINK_ID)
    ?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, MEMBRANES_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  const poolingItem = findQuestionnaireResponseItem(root.item, MEMBRANES_POOLING_LINK_ID);
  const nitrazineItem = findQuestionnaireResponseItem(root.item, MEMBRANES_NITRAZINE_LINK_ID);
  const fernResultsItem = findQuestionnaireResponseItem(root.item, MEMBRANES_FERN_RESULTS_LINK_ID);

  return {
    ...emptyValues,
    examDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    examTime: timeAnswer ?? null,
    fluidColor: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, MEMBRANES_FLUID_COLOR_LINK_ID),
    ),
    fluidColorOther: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, MEMBRANES_FLUID_COLOR_OTHER_LINK_ID),
    ),
    pooling: readYesNoDisplayFromAnswer(poolingItem?.answer?.[0]),
    nitrazine: readPositiveNegativeDisplayFromAnswer(nitrazineItem?.answer?.[0]),
    fernResults: readPositiveNegativeDisplayFromAnswer(fernResultsItem?.answer?.[0]),
    notes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, MEMBRANES_NOTES_LINK_ID),
    ),
    signatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, MEMBRANES_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

/** Nested form shape expected by `getQuestionnaireResponseFromFormValues` and labor bots. */
export function mapMembranesFormValuesToQuestionnaireValues(
  formValues: MembranesFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [MEMBRANES_ROOT_LINK_ID]: {
      [MEMBRANES_DATE_TIME_LINK_ID]: {
        [MEMBRANES_DATE_LINK_ID]: formValues.examDate,
        [MEMBRANES_TIME_LINK_ID]: formValues.examTime,
      },
      [MEMBRANES_FLUID_COLOR_LINK_ID]: parseCodingFromNativeSelect(formValues.fluidColor),
      [MEMBRANES_FLUID_COLOR_OTHER_LINK_ID]: formValues.fluidColorOther,
      [MEMBRANES_POOLING_LINK_ID]: yesNoDisplayToLoincCoding(formValues.pooling),
      [MEMBRANES_NITRAZINE_LINK_ID]: positiveNegativeDisplayToCoding(formValues.nitrazine),
      [MEMBRANES_FERN_RESULTS_LINK_ID]: positiveNegativeDisplayToCoding(formValues.fernResults),
      [MEMBRANES_NOTES_LINK_ID]: formValues.notes,
      [MEMBRANES_SIGNATURE_INITIALS_LINK_ID]: formValues.signatureInitials,
    },
  };
}
